// Client-side encryption for health-sensitive data (Tier 4).
// All encryption happens in the browser — the server never sees plaintext.
// Key derivation: PBKDF2-SHA256, 600k iterations (OWASP 2024 recommendation).
// Cipher: AES-GCM 256-bit with a fresh 12-byte random IV per encryption.

let _cachedKey = null

/**
 * Derive a 256-bit AES-GCM key from a password and base64-encoded salt.
 * The salt is stored in profiles.encryption_salt (unique per user).
 */
export async function deriveKey(password, saltBase64) {
  const saltBytes = base64ToBytes(saltBase64)
  const encoder = new TextEncoder()

  const baseKey = await crypto.subtle.importKey(
    'raw',
    encoder.encode(password),
    'PBKDF2',
    false,
    ['deriveKey']
  )

  return crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: saltBytes,
      iterations: 600_000,
      hash: 'SHA-256',
    },
    baseKey,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  )
}

/**
 * Encrypt a plaintext string. Returns a base64 string encoding IV + ciphertext.
 * A fresh random 12-byte IV is generated for every call.
 */
export async function encrypt(plaintext, key) {
  const iv = crypto.getRandomValues(new Uint8Array(12))
  const encoder = new TextEncoder()
  const ciphertext = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    key,
    encoder.encode(plaintext)
  )

  // Prepend IV to ciphertext so we can recover it on decrypt
  const combined = new Uint8Array(iv.byteLength + ciphertext.byteLength)
  combined.set(iv, 0)
  combined.set(new Uint8Array(ciphertext), iv.byteLength)
  return bytesToBase64(combined)
}

/**
 * Decrypt a base64 string produced by encrypt(). Throws on failure (bad key or tampered data).
 */
export async function decrypt(ciphertextBase64, key) {
  const combined = base64ToBytes(ciphertextBase64)
  const iv = combined.slice(0, 12)
  const ciphertext = combined.slice(12)

  const plaintext = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv },
    key,
    ciphertext
  )

  return new TextDecoder().decode(plaintext)
}

/** Returns the in-memory cached key, or null if the vault is locked. */
export function getCachedKey() {
  return _cachedKey
}

/** Store the derived key in memory for the session. Never touches localStorage. */
export function unlockHealthVault(key) {
  _cachedKey = key
}

/** Wipe the cached key. Called on signOut and vault auto-lock. */
export function lockHealthVault() {
  _cachedKey = null
}

// --- helpers ---

function bytesToBase64(bytes) {
  let binary = ''
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i])
  }
  return btoa(binary)
}

function base64ToBytes(base64) {
  const binary = atob(base64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i)
  }
  return bytes
}
